import React, { useCallback, useMemo, useState } from 'react';
import _ from 'underscore';
import { useAngularService } from 'helpers/get_angular_service';
import ReactTooltip from 'react-tooltip';
import usePermissions from 'helpers/use_permissions';
import redirect from 'helpers/redirect';
import Table from '../../components/table';
import api from '../../../helpers/api';
import sources from '../../../helpers/sources';
import Input from '../../components/input';
import { useExtendState } from '../../../helpers/use_extend_state';
import notification from '../../../helpers/notification';
import CommsTabs from '../../components/comms_tabs';
import Select from '../../components/select';
import ContactLink from '../../components/contact_link';
import dateFormatter from 'helpers/date_formatter';
import Button from '../../components/button';
import DateInput from '../../components/date_input';
import Tooltip from '../../components/tooltip';
import useOnMount from 'helpers/use_on_mount';
import useGym from 'helpers/use_gym';
import Dropdown from 'react/components/dropdown';

interface Filters {
  contact_name_or_phone: string;
  read_status?: string;
  from_date?: string;
  to_date?: string;
}

const statuses = [
  ['read_only', 'Read only'],
  ['unread_only', 'Unread only'],
];

const SmsReplies = () => {
  const $rootScope = useAngularService('$rootScope');

  const loadingAnimation = useAngularService('loadingAnimation');
  const GTMService = useAngularService('GTMService');
  const MessageService = useAngularService('MessageService');

  const gym = useGym();

  const [filters, extendFilters] = useExtendState<Filters>({ contact_name_or_phone: '' });
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const permissions = usePermissions();

  const refreshUnreadCount = useCallback(() => {
    const url = `/gyms/${gym.id}/sms_replies/unread_count`;
    api.get(url).then((data) => {
      $rootScope.unreadReplyCount = data.unread_reply_count;
      $rootScope.$emit('unread_replies:loaded', data.unread_reply_count);
    });
  }, [gym.id, $rootScope]);

  useOnMount(() => {
    GTMService.trackPage('Sms Replies');

    if (!permissions.view_sms_replies_report) {
      redirect('gym.dashboard');
      return;
    }

    refreshUnreadCount();
  });

  const count = 10;

  const getReplies = useCallback(
    (page: number) => {
      loadingAnimation.show();
      setLoading(true);
      setCurrentPage(page);
      const url = sources.smsReplies(gym.id);

      api.get(url, { page, count, filters }).then(
        (data) => {
          setLoading(false);
          setReplies(data);
          loadingAnimation.hide();

          setTimeout(() => {
            ReactTooltip.rebuild();
          }, 1000);
        },
        () => {
          loadingAnimation.hide();
          notification.error('Request could not be completed. Please try again');
        },
      );
    },
    [filters, gym.id, loadingAnimation],
  );

  const reload = useCallback(() => {
    getReplies(currentPage);
  }, [currentPage, getReplies]);

  const isDropdownAvailable = useCallback(
    (item) => {
      return permissions.delete_sms_replies || !item.unread;
    },
    [permissions.delete_sms_replies],
  );

  const remove = useCallback(
    (reply) => {
      const message = reply.linked_to_log_entry
        ? [
            'This will remove the selected SMS reply. Also, the contact activity stream will be updated to remove any reference that the reply was received.',
            'Are you sure you want to continue?',
          ]
        : [
            'This will remove the selected SMS reply. However, the contact activity stream will still contain an entry showing the original reply was received.',
            'Are you sure you want to continue?',
          ];

      MessageService.message({
        title: 'Remove SMS reply',
        message,
        displayOkButton: true,
        displayCancelButton: true,
      }).then(() => {
        const url = `${sources.smsReplies(gym.id)}/${reply.id}`;
        api.delete(url).then(
          () => {
            reload();
            if (reply.unread) {
              $rootScope.unreadReplyCount = $rootScope.unreadReplyCount - 1;
              $rootScope.$emit('unread_replies:loaded', $rootScope.unreadReplyCount);
            }
          },
          () => {
            notification.error('Request could not be completed. Please try again');
          },
        );
      });
    },
    [$rootScope, MessageService, gym.id, reload],
  );

  const markUnseen = useCallback(
    (item) => {
      const url = `/gyms/${gym.id}/sms_replies/${item.id}/mark_as_unread`;
      api.put(url).then(
        () => {
          reload();
          $rootScope.unreadReplyCount = $rootScope.unreadReplyCount + 1;
          $rootScope.$emit('unread_replies:loaded', $rootScope.unreadReplyCount);
        },
        () => {
          notification.error('Request could not be completed. Please try again');
        },
      );
    },
    [$rootScope, gym.id, reload],
  );

  const onActionSelect = useCallback(
    (action, item) => {
      switch (action) {
        case 'delete': {
          remove(item);
          break;
        }
        case 'mark_unseen': {
          markUnseen(item);
          break;
        }
        default:
          break;
      }
    },
    [remove, markUnseen],
  );

  const markAsRead = useCallback(
    (reply) => {
      if (window.confirm('Are you sure you want to mark this SMS reply as read?')) {
        const url = `${sources.smsReplies(gym.id)}/${reply.id}/mark_as_read`;
        api.put(url).then(
          () => {
            reload();
            $rootScope.unreadReplyCount = $rootScope.unreadReplyCount - 1;
            $rootScope.$emit('unread_replies:loaded', $rootScope.unreadReplyCount);
          },
          () => {
            notification.error('Request could not be completed. Please try again');
          },
        );
      }
    },
    [$rootScope, gym.id, reload],
  );

  const markAllAsRead = () => {
    if (window.confirm('Are you sure?')) {
      const url = `${sources.smsReplies(gym.id)}/mark_all_as_read`;
      api.post(url).then(
        () => {
          reload();
          $rootScope.unreadReplyCount = 0;
          $rootScope.$emit('unread_replies:loaded', $rootScope.unreadReplyCount);
        },
        () => {
          notification.error('Request could not be completed. Please try again');
        },
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Contact',
        id: 'contact',
        Cell: ({ row }) => (
          <ContactLink
            name={row.original.contact_name}
            id={row.original.contact_id}
            type={row.original.contact_type}
            status={row.original.contact_status}
            archived={row.original.contact_archived}
          />
        ),
      },
      {
        Header: 'Phone Number',
        id: 'contact_number',
        Cell: ({ row }) => <div>{row.original.contact_number}</div>,
      },
      {
        Header: 'Reply Received',
        id: 'created_at',
        Cell: ({ row }) => (
          <div>
            <div className="nowrap">{dateFormatter(row.original.created_at, 'date')} </div>
            <div className="nowrap">{dateFormatter(row.original.created_at, 'time')}</div>
          </div>
        ),
      },
      {
        Header: 'Message',
        id: 'message',
        Cell: ({ row }) => <div>{row.original.body}</div>,
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) => (
          <div className="text-right nowrap">
            {row.original.unread && (
              <Button
                variant="icon--v2"
                onClick={() => markAsRead(row.original)}
                tooltip="Mark as read"
              >
                <i className="icon-preview" />
              </Button>
            )}

            {isDropdownAvailable(row.original) && (
              <Dropdown
                options={_.compact([
                  !row.original.unread && {
                    value: 'mark_unseen',
                    label: 'Mark as unread',
                  },

                  permissions.delete_sms_replies && {
                    value: 'delete',
                    label: 'Delete',
                  },
                ])}
                onSelect={(action) => onActionSelect(action, row.original)}
                buttonType="icon-more"
              />
            )}
          </div>
        ),
      },
    ],
    [isDropdownAvailable, markAsRead, onActionSelect, permissions.delete_sms_replies],
  );

  const getRowProps = (row) => {
    const className = row.original.unread ? 'unread' : '';
    return { className };
  };

  return (
    <section className="content-section">
      <header className="page-header">
        <CommsTabs tab="sms_replies" />
      </header>
      <Tooltip type="light" />

      <div className="container container--no-padding-top">
        <main>
          <div className="filters-wrapper filters-wrapper--row filters-wrapper--v2">
            <div>
              <Select
                options={statuses}
                getLabel={(item) => item[1]}
                getValue={(item) => item[0]}
                value={filters.read_status}
                onChange={(read_status) => {
                  extendFilters({ read_status });
                }}
                allowEmptyOption
                emptyLabel="All statuses"
                size="sm"
                variant="v2"
              />
            </div>

            <div className="date-range date-range--v2">
              <DateInput
                value={filters.from_date}
                placeholder="From"
                onChange={(from_date) => extendFilters({ from_date })}
                variant="36"
              />

              <DateInput
                value={filters.to_date}
                placeholder="To"
                onChange={(to_date) => extendFilters({ to_date })}
                variant="36"
              />
            </div>

            <Button className="pull-right" size="sm" variant="hollow" onClick={markAllAsRead}>
              Mark all as read
            </Button>
          </div>

          <div className="table-header">
            <Input
              value={filters.contact_name_or_phone}
              onChange={(contact_name_or_phone) => extendFilters({ contact_name_or_phone })}
              placeholder="Search by name or phone number"
            />
          </div>

          <Table
            columns={columns}
            data={replies}
            loading={loading}
            emptyText="No SMS replies found"
            pageSize={count}
            loadData={(page) => getReplies(page)}
            filters={filters}
            withSearchBar
            variant="v2"
            getRowProps={getRowProps}
          />
        </main>
      </div>
    </section>
  );
};

angular.module('gym').value('SmsReplies', SmsReplies);
